import React, { useEffect, useState } from "react";
import { Lock, ShieldCheck } from "lucide-react";
import { Fld, Loading, ErrorState, Chip } from "../ui/primitives.jsx";
import { useToast } from "../ui/Toasts.jsx";
import { useAuth } from "../auth/AuthProvider.jsx";
import { fmtDate } from "../lib/format.js";
import { useSettings, useSaveSettings, useProfiles, useSaveProfile, changeOwnPassword } from "./api.js";

export default function SettingsTab() {
  const { user, profile, isAdmin } = useAuth();
  const { notify } = useToast();

  const settings = useSettings();
  const saveSettings = useSaveSettings();
  const profiles = useProfiles();
  const saveProfile = useSaveProfile();

  const [s, setS] = useState(null);
  const [companyErr, setCompanyErr] = useState("");
  const [pw, setPw] = useState({ cur: "", next: "", conf: "" });
  const [pwErr, setPwErr] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  useEffect(() => {
    if (settings.data && !s) setS(settings.data);
  }, [settings.data, s]);

  if (settings.isPending) return <div className="card"><div className="card-b"><Loading what="settings" /></div></div>;
  if (settings.error) {
    return (
      <div className="card">
        <div className="card-b"><ErrorState error={settings.error} onRetry={() => settings.refetch()} /></div>
      </div>
    );
  }

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  async function onSaveCompany() {
    setCompanyErr("");
    try {
      await saveSettings.mutateAsync({
        company: s.company,
        phone: s.phone,
        email: s.email,
        addr: s.addr,
        delivery_note: s.delivery_note,
        tax_rate: Number(s.tax_rate) || 0,
      });
      notify("Company info saved.");
    } catch (e) {
      setCompanyErr(e.message);
    }
  }

  async function onChangePassword() {
    setPwErr("");
    if (!pw.cur) return setPwErr("Enter your current password.");
    if (pw.next.length < 8) return setPwErr("New password needs at least 8 characters.");
    if (pw.next !== pw.conf) return setPwErr("New passwords don't match.");
    setPwBusy(true);
    try {
      await changeOwnPassword({ email: user.email, currentPassword: pw.cur, newPassword: pw.next });
      setPw({ cur: "", next: "", conf: "" });
      notify("Password changed.");
    } catch (e) {
      setPwErr(e.message);
    } finally {
      setPwBusy(false);
    }
  }

  async function onRoleChange(p, role) {
    try {
      await saveProfile.mutateAsync({
        id: p.id,
        patch: { role },
        currentUserId: profile?.id,
        profiles: profiles.data ?? [],
      });
      notify(`${p.name} is now ${role}.`);
    } catch (e) {
      notify(e.message);
    }
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", gap: 18 }}>
      <div className="card">
        <div className="card-h"><h2>My login</h2></div>
        <div className="card-b">
          <div className="frm" style={{ gridTemplateColumns: "1fr" }}>
            <div style={{ fontSize: 13.5 }}>
              Signed in as <b>{profile?.name}</b> ({user?.email}) · {isAdmin ? "Admin" : "Staff"}
            </div>
            {pwErr && <div className="notice red" style={{ margin: 0 }}>{pwErr}</div>}
            <Fld label="Current password">
              <input type="password" autoComplete="current-password" value={pw.cur}
                onChange={(e) => setPw({ ...pw, cur: e.target.value })} />
            </Fld>
            <Fld label="New password" hint="At least 8 characters.">
              <input type="password" autoComplete="new-password" value={pw.next}
                onChange={(e) => setPw({ ...pw, next: e.target.value })} />
            </Fld>
            <Fld label="Confirm new password">
              <input type="password" autoComplete="new-password" value={pw.conf}
                onChange={(e) => setPw({ ...pw, conf: e.target.value })} />
            </Fld>
            <button className="btn dark" onClick={onChangePassword} disabled={pwBusy} style={{ justifySelf: "start" }}>
              <Lock size={15} /> {pwBusy ? "Changing…" : "Change password"}
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-h"><h2>Company info</h2></div>
        <div className="card-b">
          {companyErr && <div className="notice red">{companyErr}</div>}
          <div className="frm">
            <Fld full label="Business name">
              <input value={s?.company ?? ""} onChange={(e) => set("company", e.target.value)} disabled={!isAdmin} />
            </Fld>
            <Fld label="Phone">
              <input value={s?.phone ?? ""} onChange={(e) => set("phone", e.target.value)} disabled={!isAdmin} />
            </Fld>
            <Fld label="Email">
              <input value={s?.email ?? ""} onChange={(e) => set("email", e.target.value)} disabled={!isAdmin} />
            </Fld>
            <Fld full label="Address">
              <input value={s?.addr ?? ""} onChange={(e) => set("addr", e.target.value)} disabled={!isAdmin} />
            </Fld>
            <Fld full label="Delivery note" hint="Shown on quotes and invoices.">
              <input value={s?.delivery_note ?? ""} onChange={(e) => set("delivery_note", e.target.value)} disabled={!isAdmin} />
            </Fld>
            <Fld label="Tax rate (%)">
              <input type="number" step="0.01" value={s?.tax_rate ?? ""} onChange={(e) => set("tax_rate", e.target.value)} disabled={!isAdmin} />
            </Fld>
            <Fld label="Next invoice number" hint="Assigned automatically. Read-only here so numbering cannot be knocked out of sequence.">
              <input value={s?.invoice_seq ?? ""} readOnly disabled />
            </Fld>
          </div>
          {isAdmin ? (
            <button className="btn primary" style={{ marginTop: 14 }} onClick={onSaveCompany} disabled={saveSettings.isPending}>
              {saveSettings.isPending ? "Saving…" : "Save company info"}
            </button>
          ) : (
            <div className="sub-cell" style={{ marginTop: 10 }}>Only an admin can change these.</div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-h"><h2>Who has access</h2></div>
        <div className="card-b tight">
          {profiles.isPending ? (
            <Loading what="accounts" />
          ) : profiles.error ? (
            <ErrorState error={profiles.error} onRetry={() => profiles.refetch()} />
          ) : (
            <table className="tbl">
              <thead><tr><th>Name</th><th>Role</th><th>Since</th></tr></thead>
              <tbody>
                {(profiles.data ?? []).map((p) => (
                  <tr key={p.id}>
                    <td>
                      <div className="main-cell">{p.name}</div>
                      {p.id === profile?.id && <div className="sub-cell">that's you</div>}
                    </td>
                    <td>
                      {isAdmin ? (
                        <select
                          value={p.role}
                          onChange={(e) => onRoleChange(p, e.target.value)}
                          disabled={saveProfile.isPending}
                          style={{ border: "1px solid var(--line)", borderRadius: 6, padding: "5px 8px" }}
                        >
                          <option value="staff">Staff</option>
                          <option value="admin">Admin</option>
                        </select>
                      ) : (
                        <Chip kind={p.role === "admin" ? "reserved" : "available"}>{p.role}</Chip>
                      )}
                    </td>
                    <td className="sub-cell">{p.created_at ? fmtDate(p.created_at.slice(0, 10)) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="card-b" style={{ borderTop: "1px solid var(--line)" }}>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <ShieldCheck size={17} style={{ color: "var(--green)", flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: 13 }}>
              <b>Passwords are not visible here, by design.</b> The old app listed them in
              plaintext; they are now hashed by Supabase and nobody — including an admin —
              can read them back. To reset someone's password, do it from the Supabase
              dashboard.
            </div>
          </div>
          <div style={{ fontSize: 13, marginTop: 12, color: "var(--muted)" }}>
            Adding someone is two steps: create their login in Supabase (Authentication →
            Users → Add user, with <b>Auto Confirm User</b> ticked), then run{" "}
            <code>npm run grant -- their@email.com "Their Name" staff</code>. Without the
            second step they can sign in and see nothing.
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-h"><h2>Backups</h2></div>
        <div className="card-b">
          <div style={{ fontSize: 13.5, marginBottom: 10 }}>
            The old app's "Export full backup" existed because everything lived in this
            browser — if you lost the tab, you lost the business. Your data now sits in
            Postgres, which Supabase backs up on its own schedule, so there is nothing to
            download and keep safe here.
          </div>
          <div className="notice" style={{ margin: 0 }}>
            <b>One export still matters:</b> the final cutover. Export from the v10 artifact
            one last time, then run <code>npm run migrate</code> so nothing created in the
            old app is left behind. Those backup files hold plaintext logins and customer
            records — keep them off email and out of the repo.
          </div>
        </div>
      </div>
    </div>
  );
}
